import React from "react";
import ApplicationsHistoryTable from "../layouts/ApplicationsHistoryTable";
import { useState, useEffect } from "react";
import api from "../services/api";

const ApplicationsTracking = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const authToken = sessionStorage.getItem("token");
        await api({
          url: `/api/requests_under_review/all`,
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        })
          .then((res) => {
            // Access the data array from the nested response structure
            setApplications(res.data.data || []);
          })
          .catch((err) => {
            console.log("Error fetching data:", err);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);
  const handleDetailsClick = (application) => {
    setSelectedApplication(application);
  };

  const handleCloseDialog = () => {
    setSelectedApplication(null);
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "text-orange-600";
      case "APPROVED":
        return " text-blue-600";
      case "DECLINED":
        return " text-red-600";
      default:
        return "";
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 pb-20 max-w-7xl min-h-screen">
        {loading ? (
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading applications...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8 w-full">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-2xl">📋</span>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Applications History
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Track your submitted applications and their current status
                  </p>
                </div>
              </div>
            </div>

            {/* Applications Table Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-indigo-600 text-lg">📊</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Your Applications
                  </h2>
                </div>
              </div>

              <ApplicationsHistoryTable
                applications={applications}
                onDetailsClick={handleDetailsClick}
              />
            </div>
          </div>
        )}

        {selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4">
                      <span className="text-white text-xl">📋</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        Application Details
                      </h2>
                      <p className="text-indigo-100">
                        Request Information & Status
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseDialog}
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-all"
                  >
                    <span className="text-white text-lg">×</span>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Application Overview */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Application Type
                      </label>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-indigo-600">📄</span>
                        </div>
                        <span className="text-lg font-semibold text-gray-900">
                          {selectedApplication.request_type.type}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Status
                      </label>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                          selectedApplication.status === "PENDING"
                            ? "bg-orange-100 text-orange-800"
                            : selectedApplication.status === "APPROVED"
                            ? "bg-green-100 text-green-800"
                            : selectedApplication.status === "DECLINED"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {selectedApplication.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Request Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-blue-600 text-sm">
                      ℹ️
                    </span>
                    Request Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Date Requested
                        </label>
                        <p className="text-gray-900 font-medium">
                          {new Date(
                            selectedApplication.date_requested
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      {typeof selectedApplication.student === "object" && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">
                              Applicant
                            </label>
                            <p className="text-gray-900 font-medium">
                              {selectedApplication.student.full_name}
                            </p>
                          </div>
                          {selectedApplication.student.branch && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Branch
                              </label>
                              <p className="text-gray-900 font-medium">
                                {selectedApplication.student.branch.name}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Comment
                      </label>
                      <div className="bg-white border border-gray-200 rounded-lg p-3 mt-1">
                        <p className="text-gray-700">
                          {selectedApplication.comment ||
                            "No additional comments"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Course Details */}
                {typeof selectedApplication.student === "object" &&
                  selectedApplication.student.courses &&
                  selectedApplication.student.courses.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2 text-green-600 text-sm">
                          📚
                        </span>
                        Course Information
                      </h3>
                      <div className="space-y-3">
                        {selectedApplication.student.courses.map(
                          (course, index) => (
                            <div
                              key={index}
                              className="bg-white border border-gray-200 rounded-lg p-4"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-500">
                                    Course Name
                                  </label>
                                  <p className="text-gray-900 font-medium">
                                    {course.course_term?.course?.name || "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-500">
                                    Admission Number
                                  </label>
                                  <p className="text-gray-900 font-medium">
                                    {course.admission_no || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end">
                <button
                  onClick={handleCloseDialog}
                  className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsTracking;
